import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Stack,
  Chip,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  Settings as SettingsIcon,
  AccountBalance as AccountBalanceIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { AppButton, AppPage } from '../components/design-system';
import { useSubdomain } from '../contexts/SubdomainContext';
import { buildTenantPath } from '../utils/tenantNavigation';
import { tenantBillingService } from '../services/TenantBillingService';
import type { TenantBillingSummaryResponse } from '@cnapse/shared/schemas.js';
import * as dt from '@cnapse/shared/data.types.js';

export const BillingPage: React.FC = () => {
  const [redirecting, setRedirecting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<TenantBillingSummaryResponse | null>(null);
  const { subdomain } = useSubdomain();
  const billingReturnUrl = `${window.location.origin}${buildTenantPath('/billing', subdomain)}`;

  useEffect(() => {
    void loadSummary();
  }, []);

  const loadSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await tenantBillingService.getTenantBillingSummary();
      setSummary(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handleManagePayment = async () => {
    try {
      setRedirecting(true);
      setError(null);
      const portal = await tenantBillingService.createPortalSession(billingReturnUrl);
      window.location.href = portal.url;
    } catch (err) {
      console.error('Failed to create portal session:', err);
      setError(err instanceof Error ? err.message : 'Failed to open billing portal');
      setRedirecting(false);
    }
  };

  const formatDate = (date: Date | null): string => {
    if (!date) {
      return '—';
    }
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const isEnterprise = summary?.billingMode === dt.BILLING_MODE.EnterpriseManual;

  if (loading) {
    return (
      <AppPage sx={{ maxWidth: 960, mx: 'auto', width: '100%', alignItems: 'center', py: 8 }}>
        <CircularProgress />
      </AppPage>
    );
  }

  return (
    <AppPage sx={{ maxWidth: 960, mx: 'auto', width: '100%' }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isEnterprise ? (
        <Card elevation={2}>
          <CardContent>
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon color="primary" />
                <Typography variant="h6">Enterprise billing</Typography>
                <Chip label={summary?.status ?? 'unknown'} size="small" color="primary" variant="outlined" />
              </Box>

              {summary?.packageName && (
                <Typography variant="body1">
                  <strong>Package:</strong> {summary.packageName}
                </Typography>
              )}

              <Typography variant="body2" color="text.secondary">
                Your organization is on an enterprise contract. Invoices and payment instructions are
                managed through your Cnapse representative and accounts payable process.
              </Typography>

              <Stack spacing={1.5}>
                {summary && summary.status === dt.SUBSCRIPTION_STATUS.Trialing && summary.trialEndsAt && (
                  <Alert severity="info">
                    Pilot evaluation ends on <strong>{formatDate(summary.trialEndsAt)}</strong>.
                    Contact your Cnapse representative to proceed with an annual contract.
                  </Alert>
                )}

                {summary?.contractPoNumber && (
                  <Typography variant="body2">
                    <strong>PO number:</strong> {summary.contractPoNumber}
                  </Typography>
                )}

                {summary?.currentPeriodEnd && summary.status === dt.SUBSCRIPTION_STATUS.Active && (
                  <Typography variant="body2">
                    <strong>Current license period ends:</strong> {formatDate(summary.currentPeriodEnd)}
                  </Typography>
                )}

                {summary?.invoiceDueAt && (
                  <Typography variant="body2">
                    <strong>Invoice due:</strong> {formatDate(summary.invoiceDueAt)}
                  </Typography>
                )}
              </Stack>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <EmailIcon color="primary" />
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Billing contact
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    For invoices, PO updates, or payment questions, email{' '}
                    <a href={`mailto:${summary?.contactEmail}`}>{summary?.contactEmail}</a>.
                  </Typography>
                </Box>
              </Box>

              {summary?.invoicePortalUrl && (
                <AppButton
                  variant="outlined"
                  startIcon={<ReceiptIcon />}
                  href={summary.invoicePortalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View invoice history
                </AppButton>
              )}
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card elevation={2}>
            <CardContent>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Stripe Customer Portal
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Access the Stripe Customer Portal to manage all aspects of your billing and subscription.
                  </Typography>
                </Box>

                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CreditCardIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Payment Methods
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Add or update bank accounts (ACH) or credit cards in the Stripe portal
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <AccountBalanceIcon color="success" />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        ACH / bank transfer (recommended)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Standard subscription rate with no card processing fee on invoices
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CreditCardIcon color="warning" />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Credit card payments
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Allowed if you prefer cards, but each invoice includes a separate card
                        processing fee line (currently 3% of pre-fee subtotal)
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <ReceiptIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Invoices & Receipts
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        View and download past invoices and payment receipts
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <TrendingUpIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Usage & Billing
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Monitor your usage and upcoming charges
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <SettingsIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Subscription Settings
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Update your plan or manage subscription settings
                      </Typography>
                    </Box>
                  </Box>
                </Stack>

                {summary?.showStripePortal && (
                  <Box sx={{ pt: 2 }}>
                    <AppButton
                      variant="contained"
                      size="medium"
                      fullWidth
                      startIcon={redirecting ? <CircularProgress size={20} color="inherit" /> : <CreditCardIcon />}
                      onClick={() => void handleManagePayment()}
                      disabled={redirecting}
                    >
                      {redirecting ? 'Opening Stripe Portal...' : 'Open Billing Portal'}
                    </AppButton>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Alert severity="info">
            <Typography variant="body2" component="div">
              <strong>Payment policy:</strong> Subscription prices are quoted at our standard ACH rate.
              Credit card payments are supported, but Stripe invoices include a separate processing fee
              for credit cards only (not debit cards or ACH). To avoid the fee, set a US bank account as
              your default payment method in the portal.
            </Typography>
          </Alert>

          <Alert severity="info">
            <Typography variant="body2">
              <strong>Secure Portal:</strong> You&apos;ll be redirected to Stripe&apos;s secure customer portal where you
              can manage all billing-related settings. You can return at any time.
            </Typography>
          </Alert>
        </>
      )}
    </AppPage>
  );
};

export default BillingPage;
